import React, { useEffect } from 'react';
import { X, Mail, Phone, Calendar, Globe, MapPin, Building2, GraduationCap, FileText, Download, User } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const StudentDrawer = ({ student, onClose }) => {
  const { toast } = useToast();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const downloadDoc = async (idx, name) => {
    try {
      const r = await api.get(`/recruiter/students/${student.id}/documents/${idx}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = name || 'document'; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast({ title: 'Download failed' }); }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed top-0 right-0 z-50 w-full max-w-lg h-full bg-white shadow-2xl overflow-y-auto">
        <div className="p-6 border-b border-[#0B5E2A]/10 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#6b7e6d] font-semibold">Student Profile</div>
              <h3 className="font-display text-2xl text-[#0B5E2A]">{student.full_name}</h3>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded hover:bg-[#f0f6ee]"><X className="h-5 w-5 pointer-events-none" /></button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#DEE9FA] text-[#1e40af]">{(student.application_stage || '').toUpperCase()}</span>
            {student.assigned_college_name && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#E1F3E4] text-[#0B5E2A]"><Building2 className="h-3.5 w-3.5" /> {student.assigned_college_name}</span>
            )}
          </div>
        </div>
        <div className="p-6 space-y-4">
          <Row icon={Mail} label="Email" value={student.email} />
          <Row icon={Phone} label="Phone" value={`${student.phone_code} ${student.phone}`} />
          <Row icon={Calendar} label="Date of Birth" value={student.dob} />
          <Row icon={User} label="Gender" value={student.gender || '—'} />
          <Row icon={Globe} label="Nationality" value={student.nationality} />
          <Row icon={MapPin} label="Address" value={student.address || '—'} />
          <Row icon={Globe} label="Status in Canada" value={student.passport_pr_status} />
          {student.assigned_college_name && student.recruiter_commission_snapshot > 0 && (
            <div className="rounded-lg border border-[#F0B93C]/40 bg-[#FEF8E6] p-4" data-testid="recruiter-student-commission">
              <div className="text-xs font-semibold text-[#8a5e00] uppercase tracking-wider mb-2">Your Commission</div>
              <div className="flex items-end justify-between">
                <div>
                  <div className="font-display text-2xl font-bold text-[#0B5E2A]">${Number(student.recruiter_commission_snapshot).toLocaleString()}</div>
                  <div className="text-[11px] text-[#8a5e00] mt-0.5">
                    {student.application_stage === 'enrolled' ? 'Paid on enrollment' : 'Unlocks when this student enrolls'}
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${student.application_stage === 'enrolled' ? 'bg-[#E1F3E4] text-[#0B5E2A]' : 'bg-white text-[#8a5e00] border border-[#F0B93C]/40'}`}>
                  {student.application_stage === 'enrolled' ? 'PAID' : 'PENDING'}
                </span>
              </div>
            </div>
          )}
          {student.assigned_program_name && <Row icon={GraduationCap} label="Assigned Program" value={student.assigned_program_name} />}
          {student.preferred_program && <Row icon={GraduationCap} label="Preferred Program" value={student.preferred_program} />}
          {student.preferred_intake && <Row icon={Calendar} label="Preferred Intake" value={student.preferred_intake} />}
          {student.needs_exam_prep && (
            <div className="bg-[#FEF4DB] border border-[#F0B93C]/40 rounded-md p-3">
              <div className="text-xs font-semibold text-[#8a5e00] uppercase tracking-wider mb-1">Exam Prep Required</div>
              <div className="flex flex-wrap gap-1.5">{(student.exam_prep_types || []).map((t) => <span key={t} className="px-2 py-0.5 bg-white rounded text-xs font-semibold text-[#8a5e00]">{t}</span>)}</div>
            </div>
          )}
          {student.notes && (
            <div>
              <div className="text-xs uppercase tracking-wider text-[#6b7e6d] font-semibold mb-1">Notes</div>
              <div className="text-sm text-[#1b2a1f] whitespace-pre-wrap bg-[#f6faf3] rounded-md p-3 border border-[#0B5E2A]/10">{student.notes}</div>
            </div>
          )}
          <div>
            <div className="text-xs uppercase tracking-wider text-[#6b7e6d] font-semibold mb-2">Documents ({(student.documents || []).length})</div>
            {(student.documents || []).length === 0 ? (
              <div className="text-sm text-[#6b7e6d] italic">No documents uploaded.</div>
            ) : (
              <ul className="space-y-2">
                {(student.documents || []).map((d, i) => (
                  <li key={i} className="flex items-center gap-3 bg-[#f6faf3] border border-[#0B5E2A]/10 rounded-md px-3 py-2.5">
                    <FileText className="h-4 w-4 brand-green" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm font-medium">{d.name}</div>
                      <div className="text-xs text-[#6b7e6d]">{d.doc_type} · {Math.ceil((d.size || 0)/1024)} KB</div>
                    </div>
                    <button type="button" onClick={() => downloadDoc(i, d.name)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee] transition-colors">
                      <Download className="h-3.5 w-3.5 pointer-events-none" /> Download
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="h-8 w-8 rounded-md bg-[#f0f6ee] flex items-center justify-center shrink-0"><Icon className="h-4 w-4 brand-green" /></div>
    <div><div className="text-[11px] uppercase tracking-wider text-[#6b7e6d] font-semibold">{label}</div><div className="text-sm text-[#1b2a1f]">{value}</div></div>
  </div>
);

export default StudentDrawer;
